import React from 'react';
import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormControl } from "@/components/ui/form";
import { Slider } from "@/components/ui/slider";

interface ExperienceScalesProps {
  form: UseFormReturn<any>;
  isHidden?: boolean;
}

// Helper function to compute the dynamic label based on the slider value
const getLabelForPercent = (percent: number, leftSideLabel: string, rightSideLabel: string) => {
  if (percent < 1) {
    return `Entirely ${leftSideLabel}`;
  } else if (percent < 15) {
    return `Almost entirely ${leftSideLabel}`;
  } else if (percent < 30) {
    return `Mostly ${leftSideLabel}`;
  } else if (percent < 47) {
    return `More ${leftSideLabel}`;
  } else if (percent < 53) {
    return "About even";
  } else if (percent < 70) {
    return `More ${rightSideLabel}`;
  } else if (percent < 85) {
    return `Mostly ${rightSideLabel}`;
  } else if (percent < 99) {
    return `Almost entirely ${rightSideLabel}`;
  } else {
    return `Entirely ${rightSideLabel}`;
  }
};

export const ExperienceScales = ({ form, isHidden = false }: ExperienceScalesProps) => {
  return (
    <div className={`space-y-8 mt-6 ${isHidden ? 'opacity-50' : ''}`}>
      <FormField
        control={form.control}
        name="campExperiences.0.outdoorTimeScale"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-4 mb-2">
              <span className="text-sm font-medium w-28">Indoor time</span>
              <FormControl className="flex-1">
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[field.value]}
                  onValueChange={([value]) => field.onChange(value)}
                />
              </FormControl>
              <span className="text-sm font-medium w-28 text-left">Outdoor time</span>
            </div>
            <div className="flex justify-center">
              <div className="text-sm mt-1 text-muted-foreground ml-2 w-28">Current value: </div>
              <div className="text-sm mt-1 text-base font-bold text-left w-48">
                {getLabelForPercent(field.value, "indoor time", "outdoor time")}
              </div>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="campExperiences.0.structuredTimeScale"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-4 mb-2">
              <span className="text-sm font-medium w-28">Free play</span>
              <FormControl className="flex-1">
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[field.value]}
                  onValueChange={([value]) => field.onChange(value)}
                />
              </FormControl>
              <span className="text-sm font-medium w-28 text-left">Structured time</span>
            </div>
            <div className="flex justify-center">
              <div className="text-sm mt-1 text-muted-foreground ml-2 w-28">Current value: </div>
              <div className="text-sm mt-1 text-base font-bold text-left w-48">
                {getLabelForPercent(field.value, "free play", "structured time")}
              </div>
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="campExperiences.0.physicalityScale"
        render={({ field }) => (
          <FormItem>
            <div className="flex items-center gap-4 mb-2">
              <span className="text-sm font-medium w-28">Physically mild</span>
              <FormControl className="flex-1">
                <Slider
                  min={0}
                  max={100}
                  step={1}
                  value={[field.value]}
                  onValueChange={([value]) => field.onChange(value)}
                />
              </FormControl>
              <span className="text-sm font-medium w-28 text-left">Physically intense</span>
            </div>
            <div className="flex justify-center">
              <div className="text-sm mt-1 text-muted-foreground ml-2 w-28">Current value: </div>
              <div className="text-sm mt-1 text-base font-bold text-left w-48">
                {getLabelForPercent(field.value, "physically mild", "physically intense")}
              </div>
            </div>
          </FormItem>
        )}
      />
    </div>
  );
};
